// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Button, Modal, Stack, Text, Textarea, TextInput } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { createReference } from '@medplum/core';
import type { Communication, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { MultiResourceInput, ResourceInput, useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { showErrorNotification } from '../../utils/notifications';
import { withTenantScope } from '../../utils/tenant';

interface NewTopicDialogProps {
  subject: Reference<Patient> | Patient | undefined;
  opened: boolean;
  onClose: () => void;
  onSubmit?: (communication: Communication) => void;
}

export const NewTopicDialog = (props: NewTopicDialogProps): JSX.Element => {
  const { subject, opened, onClose, onSubmit } = props;
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const profileRef = useMemo(() => (profile ? createReference(profile) : undefined), [profile]);

  const [topic, setTopic] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [practitioners, setPractitioners] = useState<Reference<Practitioner>[]>(() =>
    profile?.resourceType === 'Practitioner' ? [createReference(profile) as Reference<Practitioner>] : []
  );
  const [patient, setPatient] = useState<Reference<Patient> | undefined>(
    subject ? createReference(subject as Patient) : undefined
  );

  useEffect(() => {
    setPatient(subject ? createReference(subject as Patient) : undefined);
    setTopic('');
    setBody('');
  }, [subject]);

  useEffect(() => {
    if (profile?.resourceType !== 'Practitioner') {
      return;
    }

    setPractitioners((current) =>
      current.length > 0 ? current : [createReference(profile) as Reference<Practitioner>]
    );
  }, [profile]);

  const hasPractitioner = practitioners.length > 0;
  const canSubmit =
    Boolean(patient) && hasPractitioner && topic.trim().length > 0 && body.trim().length > 0 && !submitting;

  const handleSubmit = async (): Promise<void> => {
    if (!patient) {
      showNotification({
        title: 'Error',
        message: 'Please select a patient',
        color: 'red',
      });
      return;
    }
    if (!topic.trim()) {
      showNotification({ title: 'Error', message: 'Please enter a topic', color: 'red' });
      return;
    }
    if (!body.trim()) {
      showNotification({ title: 'Error', message: 'Please enter a message', color: 'red' });
      return;
    }

    const recipient = [patient, ...practitioners.map((practitioner) => ({ reference: practitioner.reference }))];

    const parent: Communication = {
      resourceType: 'Communication',
      status: 'in-progress',
      subject: patient,
      sender: profileRef,
      recipient,
      topic: { text: topic.trim() },
    };

    setSubmitting(true);
    try {
      const createdParent = await medplum.createResource(withTenantScope(parent));

      const firstMessage: Communication = {
        resourceType: 'Communication',
        status: 'in-progress',
        subject: patient,
        sender: profileRef,
        recipient,
        partOf: [{ reference: `Communication/${createdParent.id}` }],
        payload: [{ contentString: body.trim() }],
        sent: new Date().toISOString(),
      };

      try {
        await medplum.createResource(withTenantScope(firstMessage));
      } catch (childError) {
        // Parent exists; surface the error and let the user retry from the thread view.
        showErrorNotification(childError);
      }

      onSubmit?.(createdParent);
      onClose();
    } catch (error) {
      showErrorNotification(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="New Message"
      size="xl"
      centered
      radius="lg"
      styles={{
        title: { fontSize: '1.5rem', fontWeight: 700 },
        body: { paddingTop: 8 },
      }}
    >
      <Stack gap="lg">
        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Patient
          </Text>
          <Text size="xs" c="dimmed">
            Select a patient
          </Text>

          <ResourceInput
            key={patient?.reference}
            resourceType="Patient"
            name="patient"
            required
            defaultValue={patient}
            onChange={(value) => {
              setPatient(value ? (createReference(value) as Reference<Patient>) : undefined);
            }}
          />
        </Stack>

        <Stack gap="xs">
          <div>
            <Text size="sm" fw={600}>
              Practitioner
            </Text>
            <Text size="xs" c="dimmed">
              Select at least one practitioner
            </Text>
          </div>

          <MultiResourceInput<Practitioner>
            key={profile?.id ?? 'practitioners'}
            resourceType="Practitioner"
            name="practitioners"
            placeholder="Select practitioners"
            defaultValue={practitioners}
            onChange={(values) => {
              setPractitioners(values.map((value) => createReference(value) as Reference<Practitioner>));
            }}
          />
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Topic
          </Text>
          <Text size="xs" c="dimmed">
            Enter a topic for the message
          </Text>

          <TextInput placeholder="Enter your topic" value={topic} onChange={(e) => setTopic(e.target.value)} required />
        </Stack>

        <Stack gap={4}>
          <Text size="sm" fw={600}>
            Message
          </Text>
          <Text size="xs" c="dimmed">
            Type your first message
          </Text>

          <Textarea
            placeholder="Write your message"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            minRows={4}
            autosize
            required
          />
        </Stack>

        <Button fullWidth size="md" mt="xs" onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
          Send
        </Button>
      </Stack>
    </Modal>
  );
};
